import axios from "axios";
import { useState } from "react";
import { HandCoins, Plus } from "lucide-react";
import "../collections.css";
import { allocationsOf, money, today, tryValue } from "../lib/format";
import { fmtDisplay } from "../lib/currency";
import { CURRENCIES } from "../lib/transactionForm";
import AllocationFields from "./AllocationFields";
import { CollectionFilters } from "./CollectionFilters";
import { CollectionTable } from "./CollectionTable";
import { useAuth } from "../context/AuthContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const METHODS = ["Havale/EFT", "Kredi Kartı", "Nakit", "Çek", "Diğer"];

const emptyForm = () => ({
  amount: "", currency: "TRY", collection_date: today(), method: "Havale/EFT",
  payer: "", payee: "", selected: [], allocs: {}, split_mode: "income",
  customer: "", description: "", income_id: "",
});

const formFrom = c => {
  const allocs = {};
  allocationsOf(c).forEach(a => { allocs[a.account] = a.amount; });
  return {
    amount: String(c.amount), currency: c.currency, collection_date: c.collection_date, method: c.method,
    payer: c.payer || "", payee: c.payee || "", selected: Object.keys(allocs), allocs, split_mode: "manual",
    customer: c.customer || "", description: c.description || "", income_id: c.income_id || "",
  };
};

const payloadOf = form => ({
  amount: Number(form.amount), currency: form.currency, collection_date: form.collection_date,
  method: form.method, payer: form.payer || null, payee: form.payee || null, customer: form.customer, description: form.description,
  allocations: (form.selected || []).map(account => ({ account, amount: Number(form.allocs?.[account] || 0) })),
  income_id: form.income_id || null,
});

function CollectionFields({ form, setField, incomes, accounts = [], payers = [], payees = [], incomeByAccount = {}, prefix }) {
  return (
    <>
      <div className="form-row">
        <div className="field">
          <label>Tutar</label>
          <input data-testid={`${prefix}-amount-input`} required type="number" min="0.01" step="0.01" placeholder="Tahsil edilen tutar"
            value={form.amount} onChange={e => setField("amount", e.target.value)}/>
        </div>
        <div className="field">
          <label>Para birimi</label>
          <select data-testid={`${prefix}-currency-select`} value={form.currency} onChange={e => setField("currency", e.target.value)}>
            {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="field">
          <label>Tarih</label>
          <input data-testid={`${prefix}-date-input`} required type="date" value={form.collection_date} onChange={e => setField("collection_date", e.target.value)}/>
        </div>
        <div className="field">
          <label>Yöntem</label>
          <select data-testid={`${prefix}-method-select`} value={form.method} onChange={e => setField("method", e.target.value)}>
            {METHODS.map(m => <option key={m} value={m}>{m}</option>)}
          </select>
        </div>
      </div>
      <AllocationFields form={form} setField={setField} accounts={accounts} payers={payers} payees={payees} incomeByAccount={incomeByAccount} prefix={prefix}/>
      <div className="field">
        <label>Cari notu <small className="hint">(isteğe bağlı)</small></label>
        <input data-testid={`${prefix}-customer-input`} placeholder="Örn. Rixos Otel" value={form.customer} onChange={e => setField("customer", e.target.value)}/>
      </div>
      <div className="field">
        <label>İlgili gelir kaydı <small className="hint">(isteğe bağlı)</small></label>
        <select data-testid={`${prefix}-income-select`} value={form.income_id} onChange={e => setField("income_id", e.target.value)}>
          <option value="">Serbest tahsilat (gelire bağlı değil)</option>
          {incomes.map(i => (
            <option key={i.id} value={i.id}>{`${i.transaction_date} · ${i.description} · ${money(i.amount, i.currency)}`}</option>
          ))}
        </select>
      </div>
      <div className="field">
        <label>Açıklama</label>
        <input data-testid={`${prefix}-description-input`} placeholder="Kısa not" value={form.description} onChange={e => setField("description", e.target.value)}/>
      </div>
    </>
  );
}

export default function CollectionsPanel({ collections, incomes, accounts = [], payers = [], payees = [], incomeByAccount = {}, month, exportMonth, filters, onFiltersChange, loading, error, rate = 0, currency = "TRY", rates = {}, onChanged }) {
  const { can } = useAuth();
  const [form, setForm] = useState(emptyForm);
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(emptyForm);
  const [editNotice, setEditNotice] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);

  const f = value => fmtDisplay(value, currency, rates);
  const setField = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const setEditField = (key, value) => setEditForm(current => ({ ...current, [key]: value }));
  const total = collections.reduce((sum, c) => sum + tryValue(c, rate), 0);
  const byMethod = METHODS
    .map(method => ({ method, total: collections.filter(c => c.method === method).reduce((s, c) => s + tryValue(c, rate), 0) }))
    .filter(x => x.total);

  const openEdit = c => {
    setEditing(c);
    setEditForm(formFrom(c));
    setEditNotice("");
  };

  const submit = async event => {
    event.preventDefault();
    if (!Number(form.amount)) { setNotice("Tahsilat tutarı sıfırdan büyük olmalı"); return; }
    if (!form.selected?.length) { setNotice("Tahsilatın hangi hesapları kapattığını seçin"); return; }
    try {
      await axios.post(`${API}/collections`, payloadOf(form));
      setForm(current => ({ ...emptyForm(), collection_date: current.collection_date, method: current.method, payer: current.payer, payee: current.payee, selected: current.selected, split_mode: current.split_mode }));
      setNotice("Tahsilat kaydedildi");
      onChanged?.();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Tahsilat eklenemedi");
    }
  };

  const saveEdit = async event => {
    event.preventDefault();
    if (!Number(editForm.amount)) { setEditNotice("Tahsilat tutarı sıfırdan büyük olmalı"); return; }
    if (!editForm.selected?.length) { setEditNotice("Tahsilatın hangi hesapları kapattığını seçin"); return; }
    try {
      await axios.put(`${API}/collections/${editing.id}`, payloadOf(editForm));
      setEditing(null);
      onChanged?.();
    } catch (e) {
      setEditNotice(e?.response?.data?.detail || "Tahsilat güncellenemedi");
    }
  };

  const confirmDelete = async () => {
    await axios.delete(`${API}/collections/${pendingDelete.id}`);
    setPendingDelete(null);
    onChanged?.();
  };

  return (
    <section className="panel collections-panel" data-testid="collections-panel">
      <div className="panel-head">
        <div>
          <p className="eyebrow">TAHSİLAT</p>
          <h3 data-testid="collections-period-title">{month} tahsilatları</h3>
        </div>
        <div className="collect-total">
          <small>Toplam tahsilat</small>
          <strong data-testid="collections-total">{loading || error ? "—" : f(total)}</strong>
        </div>
      </div>

      <CollectionFilters filters={filters} onChange={onFiltersChange} payers={payers} payees={payees} accounts={accounts}
        month={exportMonth} count={collections.length} loading={loading || !!error}/>
      {error && <div className="notice error" role="alert" data-testid="collections-load-error">{error}
        <button type="button" className="outline-btn" onClick={onChanged} data-testid="collections-retry">Tekrar dene</button>
      </div>}
      {!loading && !error && !!byMethod.length && (
        <div className="method-chips" data-testid="collections-by-method">
          {byMethod.map(m => (
            <span key={m.method} className="group-tag" data-testid={`method-total-${m.method}`}>{m.method} · {f(m.total)}</span>
          ))}
        </div>
      )}

      <div className="collect-layout">
        {loading ? <div role="status" className="empty-table" data-testid="collections-loading">Tahsilatlar yükleniyor…</div>
          : <CollectionTable collections={collections} currency={currency} rates={rates} rate={rate} onEdit={openEdit} onDelete={setPendingDelete}/>}

        {can("create") && (
          <form className="collect-form" onSubmit={submit} data-testid="collection-form">
            <div className="user-form-head"><HandCoins size={16}/> <b>Tahsilat ekle</b></div>
            <CollectionFields form={form} setField={setField} incomes={incomes} accounts={accounts} payers={payers} payees={payees}
              incomeByAccount={incomeByAccount} prefix="collection"/>
            <button className="primary-btn" type="submit" data-testid="add-collection-button">Tahsilatı kaydet <Plus size={16}/></button>
            {notice && <div className="notice" data-testid="collection-notice">{notice}</div>}
          </form>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={open => !open && setEditing(null)}>
        <DialogContent className="edit-dialog collection-edit-dialog" data-testid="edit-collection-dialog">
          <DialogHeader>
            <DialogTitle>Tahsilatı düzenle</DialogTitle>
            <DialogDescription>Tutar, tarih, yöntem, müşteri ve ilgili gelir kaydını güncelleyebilirsiniz. Kur, seçilen tarihe göre yeniden hesaplanır.</DialogDescription>
          </DialogHeader>
          {editing && (
            <form className="edit-form" onSubmit={saveEdit}>
              <CollectionFields form={editForm} setField={setEditField} accounts={accounts} payers={payers} payees={payees}
                incomeByAccount={incomeByAccount} prefix="edit-collection"
                incomes={editing.income_id && !incomes.some(i => i.id === editing.income_id)
                  ? [{ id: editing.income_id, transaction_date: "Bağlı", description: editing.income_description || "gelir kaydı", amount: 0, currency: "TRY" }, ...incomes]
                  : incomes}/>
              <div className="dialog-actions">
                <button type="button" className="outline-btn" data-testid="edit-collection-cancel" onClick={() => setEditing(null)}>Vazgeç</button>
                <button type="submit" className="primary-btn" data-testid="edit-collection-save">Değişiklikleri kaydet</button>
              </div>
              {editNotice && <div className="notice error" data-testid="edit-collection-notice">{editNotice}</div>}
            </form>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={open => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid="delete-collection-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Tahsilat silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingDelete && <><b>{pendingDelete.customer || pendingDelete.description || "Tahsilat"}</b> — {money(pendingDelete.amount, pendingDelete.currency)} ({pendingDelete.collection_date}). Günlüğe kaydedilir.</>}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-collection-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="danger-action" data-testid="delete-collection-confirm" onClick={confirmDelete}>Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
